import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProfile } from '../../../contexts/ProfileContext';
import { isRouteAccessible, SAFE_FALLBACK_ROUTES } from '../../../config/routeAccessConfig';
import { normalizePrimaryRole } from '../../../utils/roleVisibility';

interface RoleBasedRouteProps {
  children: React.ReactNode;
}

/**
 * RoleBasedRoute component that validates route access based on user's primary role.
 * 
 * If the user attempts to access a route they don't have permission for:
 * 1. Prevents navigation to that route
 * 2. Navigates back to the previous page (if browser history is available)
 * 3. Falls back to safe default route if previous page is not accessible
 * 
 * @example
 * <RoleBasedRoute>
 *   <ManageAccessPage />
 * </RoleBasedRoute>
 */
const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, loading } = useProfile();
  const [isAccessCheckComplete, setIsAccessCheckComplete] = useState(false);
  const [isAccessible, setIsAccessible] = useState(true);

  useEffect(() => {
    // Don't check access until profile is loaded
    if (loading || !profile?.primaryRole) {
      return;
    }

    const currentPathname = location.pathname;
    const normalizedRole = normalizePrimaryRole(profile.primaryRole);

    // Map normalized role to config key with fallback to 'member'
    let roleKey: 'lmp' | 'member' | 'admin' = 'member';
    if (normalizedRole === 'lmp') {
      roleKey = 'lmp';
    } else if (normalizedRole.includes('admin')) {
      roleKey = 'admin';
    }

    // Check if current route is accessible
    const isCurrentRouteAccessible = isRouteAccessible(currentPathname, roleKey);

    if (!isCurrentRouteAccessible) {
      console.warn(
        `🔒 Access Denied: User with role "${profile.primaryRole}" (${roleKey}) attempted to access ${currentPathname}. Redirecting to previous page or safe fallback...`,
      );

      // Mark route as not accessible
      setIsAccessible(false);

      // Attempt navigation: back first, fallback to safe route
      // Use setTimeout to ensure navigation happens after state updates
      setTimeout(() => {
        const canGoBack = window.history.length > 1;
        if (canGoBack) {
          navigate(-1);
        } else {
          navigate(SAFE_FALLBACK_ROUTES[roleKey], { replace: true });
        }
      }, 0);
    } else {
      setIsAccessible(true);
    }

    setIsAccessCheckComplete(true);
  }, [loading, profile?.primaryRole, location.pathname, navigate]);

  // Show nothing while loading or access check is not complete
  if (loading || !isAccessCheckComplete) {
    return null;
  }

  // If access denied, show nothing while navigation happens
  if (!isAccessible) {
    return null;
  }

  // Render children if access is allowed
  return <>{children}</>;
};

export default RoleBasedRoute;
